import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, Inbox, Mail, Phone, CalendarDays, MessageCircle, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const formatDetail = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const downloadCsv = async (path, filename) => {
  try {
    const res = await axios.get(`${API}${path}`, { withCredentials: true, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  } catch {
    toast.error("Couldn't export. Please try again.");
  }
};

const waNumber = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  return d.length === 10 ? `91${d}` : d;
};

const nudgeLink = (o) => {
  const items = (o.items || []).map((it) => `${it.name} ${it.size} ×${it.qty}`).join(", ");
  const total = `₹${(o.amount / 100).toLocaleString("en-IN")}`;
  const msg = `Hi ${o.customer?.name || "there"}! This is PhudG. We noticed you started an order (${items} — ${total}, free delivery) but couldn't complete it. No rush at all! You can finish it anytime on phudg.in, or just reply here and we'll gladly help. Thank you for choosing PhudG — good cooking begins with good ingredients. 💛`;
  return `https://wa.me/${waNumber(o.customer?.phone)}?text=${encodeURIComponent(msg)}`;
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("enquiries");

  const loadEnquiries = () => {
    axios
      .get(`${API}/enquiries`, { withCredentials: true })
      .then((res) => setEnquiries(res.data))
      .catch(() => setEnquiries([]));
  };

  const loadOrders = () => {
    axios
      .get(`${API}/payments/orders`, { withCredentials: true })
      .then((res) => setOrders(res.data))
      .catch(() => setOrders([]));
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        setUser(res.data);
        loadEnquiries();
        loadOrders();
      })
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(res.data);
      loadOrders();
      loadEnquiries();
      toast.success("Welcome back.");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setEnquiries([]);
  };

  return (
    <main data-testid="admin-page">
      <Seo title="PhudG Admin" noindex />
      <Header />
      <section className="min-h-screen bg-ink grain relative pt-32 pb-24">
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <p className="text-xs tracking-[0.4em] uppercase text-gold mb-4">PhudG Admin</p>

          {checking && <p className="text-cream/50 font-light">Checking session…</p>}

          {!checking && user === false && (
            <motion.form
              data-testid="admin-login-form"
              onSubmit={login}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-md rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-8 md:p-10"
            >
              <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-gold/10 text-gold">
                <Lock size={20} />
              </span>
              <h1 className="font-serif text-3xl text-cream mb-8">Enquiry Inbox</h1>
              <div className="space-y-4">
                <input
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full rounded-xl border border-white/15 bg-ink px-5 py-4 text-sm text-cream placeholder:text-cream/30 outline-none focus:border-gold transition-colors duration-300"
                />
                <input
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full rounded-xl border border-white/15 bg-ink px-5 py-4 text-sm text-cream placeholder:text-cream/30 outline-none focus:border-gold transition-colors duration-300"
                />
                {error && (
                  <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>
                )}
                <motion.button
                  data-testid="admin-login-btn"
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full rounded-full bg-gold px-8 py-4 text-sm font-medium text-ink hover:bg-amberbrand hover:text-cream transition-colors duration-300"
                >
                  Sign In
                </motion.button>
              </div>
            </motion.form>
          )}

          {!checking && user && (
            <div data-testid="admin-inbox">
              <div className="flex items-center justify-between mb-10">
                <h1 className="font-serif text-4xl text-cream">
                  {tab === "orders" ? "Orders" : "Enquiry Inbox"}
                  <span className="ml-4 text-base font-sans font-light text-cream/40">
                    {tab === "orders"
                      ? `${orders.length} ${orders.length === 1 ? "order" : "orders"}`
                      : `${enquiries.length} ${enquiries.length === 1 ? "message" : "messages"}`}
                  </span>
                </h1>
                <button
                  data-testid="admin-logout-btn"
                  onClick={logout}
                  className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-cream/60 hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  <LogOut size={15} /> Sign Out
                </button>
              </div>

              <div className="flex gap-3 mb-8">
                {["enquiries", "orders"].map((t) => (
                  <button
                    key={t}
                    data-testid={`admin-tab-${t}`}
                    onClick={() => setTab(t)}
                    className={`rounded-full px-5 py-2 text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                      tab === t ? "bg-gold text-ink" : "border border-white/15 text-cream/60 hover:border-gold hover:text-gold"
                    }`}
                  >
                    {t}{t === "orders" && orders.length > 0 ? ` (${orders.length})` : ""}
                  </button>
                ))}
                <button
                  data-testid={`export-${tab}-btn`}
                  onClick={() => downloadCsv(`/admin/export/${tab}.csv`, `phudg-${tab}.csv`)}
                  className="ml-auto flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 text-xs tracking-[0.2em] uppercase text-cream/60 hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  <Download size={13} /> Export CSV
                </button>
              </div>

              {tab === "orders" && (
                <div data-testid="admin-orders" className="space-y-4">
                  {orders.length === 0 && (
                    <div className="rounded-3xl border border-white/10 p-14 text-center">
                      <p className="text-cream/40 font-light">No orders yet. Paid and Cash on Delivery orders will appear here.</p>
                    </div>
                  )}
                  {orders.map((o, i) => (
                    <div key={o.id || i} data-testid={`order-item-${i}`} className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <p className="font-serif text-xl text-cream">
                          ₹{(o.amount / 100).toLocaleString("en-IN")}{" "}
                          <span className={`text-sm font-sans ${o.status === "paid" ? "text-green-400" : "text-amber-400"}`}>
                            {o.status === "cod" ? "cash on delivery" : o.status === "created" ? "abandoned — not paid" : o.status}
                          </span>
                        </p>
                        <p className="text-xs text-cream/35">
                          {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </p>
                      </div>
                      <p className="text-sm text-cream/70 mb-1">
                        {o.customer?.name} · {o.customer?.phone} · {o.customer?.email}
                      </p>
                      <p className="text-sm font-light text-cream/50 mb-3" data-testid={`order-address-${i}`}>
                        {[o.customer?.address1, o.customer?.landmark, o.customer?.city, o.customer?.state, o.customer?.pincode].filter(Boolean).join(", ") || o.customer?.address}
                      </p>
                      {(o.pricing?.bundle_discount > 0 || o.pricing?.coupon_discount > 0 || (o.pricing?.prepaid_applied && o.pricing?.prepaid_discount > 0)) && (
                        <p className="text-xs text-cream/45 mb-3" data-testid={`order-pricing-${i}`}>
                          Subtotal ₹{o.pricing.subtotal}
                          {o.pricing.bundle_discount > 0 && ` · Bundle −₹${o.pricing.bundle_discount}`}
                          {o.pricing.coupon_discount > 0 && ` · ${o.pricing.coupon_code} −₹${o.pricing.coupon_discount}`}
                          {o.pricing.prepaid_applied && o.pricing.prepaid_discount > 0 && ` · Prepaid −₹${o.pricing.prepaid_discount}`}
                        </p>
                      )}
                      <p className="text-sm text-gold/80">
                        {o.items?.map((it) => `${it.name} ${it.size} ×${it.qty}`).join("  ·  ")}
                      </p>
                      <p className="text-[11px] text-cream/30 mt-3">{o.order_id}</p>
                      {o.shiprocket?.awb && (
                        <p className="text-xs text-cream/50 mt-2" data-testid={`order-shipment-${i}`}>
                          Shipped via {o.shiprocket.courier_name} · AWB {o.shiprocket.awb} ·{" "}
                          <a href={`https://shiprocket.co/tracking/${o.shiprocket.awb}`} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2">
                            Track
                          </a>
                        </p>
                      )}
                      {o.shiprocket?.status === "FAILED" && (
                        <p className="text-xs text-red-400 mt-2" data-testid={`order-shipment-failed-${i}`}>
                          Auto-shipment failed — please book this one manually in your Shiprocket panel
                        </p>
                      )}
                      {o.status === "created" && o.customer?.phone && (
                        <a
                          data-testid={`order-nudge-${i}`}
                          href={nudgeLink(o)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-xs text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
                        >
                          <MessageCircle size={14} />
                          Nudge on WhatsApp
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {tab === "enquiries" && enquiries.length === 0 && (
                <div className="rounded-3xl border border-white/10 p-14 text-center">
                  <Inbox size={28} className="mx-auto text-cream/25 mb-4" />
                  <p data-testid="admin-inbox-empty" className="text-cream/40 font-light">
                    No enquiries yet. New messages from the contact form will appear here.
                  </p>
                </div>
              )}

              {tab === "enquiries" && (
              <div className="space-y-4">
                {enquiries.map((enq, i) => (
                  <motion.div
                    key={enq.id || i}
                    data-testid={`enquiry-item-${i}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8"
                  >
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
                      <p className="font-serif text-xl text-cream">{enq.name}</p>
                      <a href={`mailto:${enq.email}`} className="flex items-center gap-1.5 text-sm text-gold/80 hover:text-gold transition-colors duration-300">
                        <Mail size={13} /> {enq.email}
                      </a>
                      {enq.phone && (
                        <span className="flex items-center gap-1.5 text-sm text-cream/50">
                          <Phone size={13} /> {enq.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1.5 text-xs text-cream/35">
                        <CalendarDays size={13} />
                        {new Date(enq.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    </div>
                    <p className="text-sm font-light text-cream/70 leading-relaxed">{enq.message}</p>
                  </motion.div>
                ))}
              </div>
              )}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
